import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../Button/Button.js';
import { Icon } from '../Icon/Icon.js';
import { Input } from '../Input/Input.js';
import { Select } from '../Select/Select.js';
import { ThemeDropdown } from '../ThemeDropdown/ThemeDropdown.js';
import './SectionHeader.css';

export const SectionHeader = ({
  title = 'Title',
  onTitleChange,
  showThemeSelect = true,
  /** When set, show this text instead of the theme Select (no selector, no dropdown) */
  themeSelectStaticLabel,
  selectedTheme = '',
  themeOptions = [],
  onThemeChange,
  onThemeManage,
  /** When false, dropdown does not show theme actions (create/rename/delete theme) or divider above them */
  showThemeActionsInDropdown = true,
  onAddColor,
  onAddFolder,
  className = '',
  ...props
}) => {
  const baseClass = 'section-header';
  const combinedClassName = [baseClass, className].filter(Boolean).join(' ');

  const [isTitleFocused, setIsTitleFocused] = useState(false);
  const [localTitle, setLocalTitle] = useState(title);
  const [needsFallback, setNeedsFallback] = useState(() => {
    // Detect Safari immediately - use fallback for Safari as field-sizing may not work correctly
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
                     (navigator.userAgent.includes('Mac') && navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome'));
    return isSafari;
  });
  const titleInputRef = useRef(null);
  const measureSpanRef = useRef(null);

  // Local dropdown state to avoid re-rendering the whole page
  const [isThemeDropdownOpen, setIsThemeDropdownOpen] = useState(false);
  const localThemeManageButtonRef = useRef(null);

  // Prefer a ref provided via props for positioning; otherwise use a local one
  const buttonRef = onThemeManage?.buttonRef || localThemeManageButtonRef;
  const currentTheme = onThemeManage?.currentTheme;
  const handleCreateTheme = onThemeManage?.onCreateTheme;
  const handleRenameTheme = onThemeManage?.onRenameTheme;
  const handleDeleteTheme = onThemeManage?.onDeleteTheme;

  // Sync local title from prop when not focused (e.g. after blur and parent normalized empty to default)
  useEffect(() => {
    if (!isTitleFocused && title !== localTitle) {
      setLocalTitle(title);
    }
  }, [title, isTitleFocused, localTitle]);

  const displayTitle = isTitleFocused ? localTitle : title;

  // Fallback width measurement for browsers that don't support field-sizing: content (Safari)
  useEffect(() => {
    if (!needsFallback) return; // field-sizing: content is supported
    
    const input = titleInputRef.current;
    const measureSpan = measureSpanRef.current;
    
    if (!input || !measureSpan) return;

    const updateWidth = () => {
      const text = displayTitle || '';
      measureSpan.textContent = text || ' ';
      const width = measureSpan.offsetWidth;
      input.style.width = `${Math.max(width, 16)}px`; // Minimum 16px for cursor visibility
    };

    updateWidth();
    
    // Update on window resize
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [displayTitle]);

  const handleTitleFocus = () => {
    setIsTitleFocused(true);
    setLocalTitle(title);
  };

  const handleTitleChange = (e) => {
    setLocalTitle(e.target.value);
    
    // Update width immediately while typing (Safari fallback)
    if (needsFallback) {
      const input = titleInputRef.current;
      const measureSpan = measureSpanRef.current;
      if (input && measureSpan) {
        const text = e.target.value || '';
        measureSpan.textContent = text || ' ';
        const width = measureSpan.offsetWidth;
        input.style.width = `${Math.max(width, 16)}px`;
      }
    }
  };

  const handleTitleBlur = () => {
    setIsTitleFocused(false);
    if (onTitleChange) {
      onTitleChange(localTitle);
    }
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    } else if (e.key === 'Escape') {
      setLocalTitle(title);
      setIsTitleFocused(false);
      e.target.blur();
    }
  };

  return (
    <header className={combinedClassName} {...props}>
      <div className="section-header__content">
        {/* Title and Theme */}
        <div className="section-header__left">
          <div className="section-header__title-wrapper">
            {/* Hidden span for measuring text width (Safari fallback) */}
            {needsFallback && (
              <span
                ref={measureSpanRef}
                className="section-header__title-measure"
                aria-hidden="true"
              />
            )}
            <Input
              ref={titleInputRef}
              style="ghost"
              value={displayTitle}
              onChange={onTitleChange ? handleTitleChange : undefined}
              onFocus={onTitleChange ? handleTitleFocus : undefined}
              onBlur={onTitleChange ? handleTitleBlur : undefined}
              onKeyDown={handleTitleKeyDown}
              readOnly={!onTitleChange}
              className="section-header__title-input"
            />
          </div>
          {(showThemeSelect || themeSelectStaticLabel) && (
            <div className="section-header__theme">
              {themeSelectStaticLabel ? (
                <div className="section-header__theme-placeholder" aria-hidden="true">
                  {themeSelectStaticLabel}
                </div>
              ) : (
                <Select
                  type="default"
                  style="outline"
                  value={selectedTheme}
                  options={themeOptions}
                  onChange={onThemeChange}
                  placeholder="Theme"
                  className="section-header__theme-select"
                  showSectionHeaders={false}
                />
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="section-header__actions">
          {/* Add color button */}
          <Button
            type="ghost"
            size="medium"
            icon={<Icon name="add" size="medium" />}
            onClick={onAddColor}
            disabled={!onAddColor}
            className="section-header__action-button tooltip"
            data-tooltip="Add color"
          />

          {/* Add folder button */}
          <Button
            type="ghost"
            size="medium"
            icon={<Icon name="create-new-folder" size="medium" />}
            onClick={onAddFolder}
            disabled={!onAddFolder}
            className="section-header__action-button tooltip"
            data-tooltip="Add folder"
          />

          {/* Theme / collection actions dropdown */}
          <div className="section-header__actions-dropdown">
            <Button
              ref={buttonRef}
              type="ghost"
              size="medium"
              icon={<Icon name="more-vert" size="medium" />}
              onClick={onThemeManage?.onClick || (() => setIsThemeDropdownOpen((prev) => !prev))}
              className="section-header__theme-manage tooltip"
              data-tooltip="More"
            />

            {isThemeDropdownOpen && (
              <ThemeDropdown
                isOpen={isThemeDropdownOpen}
                onClose={() => setIsThemeDropdownOpen(false)}
                onCreateTheme={handleCreateTheme}
                onRenameTheme={handleRenameTheme}
                onDeleteTheme={handleDeleteTheme}
                currentTheme={currentTheme}
                buttonRef={buttonRef}
                hasThemeOptions={showThemeSelect && showThemeActionsInDropdown}
                onRenameCollection={() => {
                  if (titleInputRef.current) {
                    titleInputRef.current.focus();
                    if (typeof titleInputRef.current.select === 'function') {
                      titleInputRef.current.select();
                    }
                  }
                }}
              />
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
